<template>
  <div class="bg-secondary-1" :class="isMobile && 'row'">
    <div class="p-1" :class="isMobile && 'col-12'">
      <VisitCalendarDateRangeSelector
        :academic-year-info="academicYearInfo"
        :show-week-range="true"
        :view-mode="currentViewMode"
        :year="year"
        @on-datarange-changed="onDatarangeChanged"
      />
    </div>
    <div class="pt-2 bg-white">
      <div class="row">
        <div class="col-12 col-lg-4 mb-2 mb-md-0">
          <visit-calendar-mode
            v-model="currentViewMode"
            @on-viewmode-changed="onViewModeChanged"
            @value-day-week="onCalendarViewChange"
          />
        </div>
        <div class="col-12 col-lg-8">
          <visit-calendar-day
            :dates="dates"
            :days="days"
            :is-mobile="isMobile"
            :value="currentViewMode"
          />
        </div>
      </div>
    </div>
    <div class="bg-white">
      <div class="row">
        <div
          v-for="student in filteredStudents"
          :key="student.hour.label"
          class="col-12"
        >
          <custom-pill
            class="col-12 col-md-2 gray-12"
            :text="student.hour.label"
          />

          <div class="row container-flex student-box">
            <VisitStudentRow
              v-for="studentItem in student.students"
              :key="studentItem.id"
              ref="rowRef"
              :academic-year-id="academicYearId"
              class="mb-3 col-sm-12"
              :days="days"
              :end-date="student.endDate"
              :hour="student.hour"
              :is-mobile="isMobile"
              :justification-options="justificationOptions"
              :months-period-finalized="monthsPeriodFinalized"
              :start-date="student.startDate"
              :types="visitTypes"
              :value="studentItem"
              :view-mode="currentViewMode"
              @download-absence-document="onDownloadAbsenceDocument"
              @save-edition="onSaveEdition"
              @update-schedule="onUpdateSchedule"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VisitCalendarDateRangeSelector from './VisitCalendarDateRangeSelector';
import VisitCalendarMode from './VisitCalendarViewMode';
import VisitCalendarDay from './VisitCalendarDay';
import VisitStudentRow from './VisitStudentRow.vue';
import moment from 'moment';
import 'moment/locale/es-do';
import visitRegistryService from '@/modules/visitRegistry/api';
import documentService from '@/modules/app/api';
import { serialize as objectToFormData } from 'object-to-formdata';
import alertMixin from '@/mixins/alert.mixin.js';
import CustomPill from 'custom-pill';
import calendarViewModes from '@/utils/shared/constants/calendarViewModes';
import academicYearApi from '@/modules/common/academicyear.api';

export default {
  name: 'VisitCalendar',
  components: {
    VisitCalendarDateRangeSelector,
    VisitCalendarMode,
    VisitCalendarDay,
    VisitStudentRow,
    CustomPill,
  },
  mixins: [alertMixin],
  props: {
    academicYearId: {
      type: Number,
      default: () => null,
    },
    days: {
      type: Array,
      default: () => [
        { id: 1, name: 'Lunes', dayNumber: null, month: null },
        { id: 2, name: 'Martes', dayNumber: null, month: null },
        { id: 3, name: 'Miércoles', dayNumber: null, month: null },
        { id: 4, name: 'Jueves', dayNumber: null, month: null },
        { id: 5, name: 'Viernes', dayNumber: null, month: null },
        { id: 6, name: 'Sábado', dayNumber: null, month: null },
        { id: 0, name: 'Domingo', dayNumber: null, month: null },
      ],
    },
    students: {
      type: Array,
      default: () => [],
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    dates: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      selectedDay: null,
      year: null,
      academicYearInfo: null,
      page: {
        currentPage: 1,
      },
      currentViewMode: calendarViewModes.Week,
      visitTypes: [],
      justificationOptions: [],
    };
  },
  computed: {
    filteredStudents() {
      return this.students.filter((f) => f.students.length > 0);
    },
    monthsPeriodFinalized() {
      let visitMonthNumber =
        this.filteredStudents
          ?.flatMap((x) => x.students?.flatMap((s) => s.visits))
          ?.filter((x) => x.isPeriodFinalized)
          ?.map((x) => x?.visitMonthNumber) ?? [];

      // return list of months without duplicated monthNumber
      return [...new Set(visitMonthNumber)];
    },
  },
  watch: {
    async academicYearId(newValue) {
      if (newValue > 0) {
        const response = await academicYearApi.getAcademicYearById(
          this.academicYearId
        );
        this.year = response.year - 1;
        this.academicYearInfo = response;
      }
    },
  },
  mounted() {
    this.getVisitTypes();
  },
  methods: {
    setDaysNumber(startDate, endDate) {
      const startOfWeek = moment(startDate).startOf('isoWeek');
      const endOfWeek = moment(endDate).endOf('isoWeek');
      let currentDate = startOfWeek.clone();
      let iterator = 0;
      while (currentDate.isSameOrBefore(endOfWeek)) {
        this.days[iterator].dayNumber = currentDate.date();
        this.days[iterator].month = currentDate.month();
        currentDate.add(1, 'day');
        iterator++;
      }
    },
    onViewModeChanged(selectedMode) {
      this.currentViewMode = selectedMode;
    },
    onDatarangeChanged(rangeData) {
      this.setDaysNumber(rangeData.startDate, rangeData.endDate);
      this.$emit('on-datarange-changed', rangeData);
    },
    async getVisitTypes() {
      const { data } = await visitRegistryService.getVistTypes();
      const {
        data: justificationOptions,
      } = await visitRegistryService.getVisitAbsenceRegistration();
      this.visitTypes = data;
      this.justificationOptions = justificationOptions;
    },
    async onDownloadAbsenceDocument(id) {
      await documentService.downloadDocumentByAttachmentId(id);
    },
    async onSaveEdition(input) {
      const formData = {
        id: input.id,
        visitTypeId: input.type.id,
        absenceReasonId: input.justification.id,
        file: input.document,
        comment: input.comment,
      };
      const payload = objectToFormData(formData, { indices: true });
      const { data } = await visitRegistryService.updateRegistryVisit(payload);
      if (data) {
        this.ShowToast(
          'Proceso Exitoso',
          'La visita fue editada exitosamente.',
          'success'
        );
        this.$emit('reload');
      }
    },
    async onUpdateSchedule(payload) {
      const { data } = await visitRegistryService.updateRegistryVisitSchedule(
        payload
      );
      if (data) {
        this.ShowToast(
          'Proceso Exitoso',
          'El horario de visita fue editado exitosamente.',
          'success'
        );
        this.$emit('reload');
      }
    },
    onCalendarViewChange(dayId) {
      this.currentViewMode = dayId;
    },
  },
};
</script>
<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';

.container-flex {
  display: flex;
  justify-content: space-between;
}
.custom-border-hours {
  border-radius: 30px !important;
  font-weight: 700 !important;
  cursor: pointer !important;
  border: 1px solid #dee2e6 !important;
}
.relative-container {
  position: relative;
}
.floating-div-container {
  position: absolute;
  right: 0;
}

.floating-div {
  display: inline-block;
  border: 1px solid #ccc;
}
.add-margin-top {
  margin-top: -30px !important;
}
.student-box {
  margin-top: 20px !important;
}

.filter-selection-tag {
  border-radius: 30px;
  font-weight: 100 !important;
  background-color: map-get($colors, 'secondary-26');
}

.bg-gray {
  background-color: map-get($colors, 'secondary-3');
}

.gray-12 {
  background-color: map-get($colors, 'gray-12');
  color: white;
}
</style>
